import {
  type MinDepositTarget,
  type MinTransferConfig,
  getConfiguredMinDepositUsd,
  getMinTransferValueUsd,
} from '@funkit/connect-core'
import type { TFunction } from 'i18next'

import { chainIconUri, chainName } from '../utils/chainDisplay'
import type { TokenChainFilter } from './supportedTokens'

/** A curated deposit chain the user can pick to send funds on. */
export interface AvailableChain {
  /** Numeric chain id as a string. */
  id: string
  /** Display name, e.g. `Ethereum`, `Binance Smart Chain`. */
  name: string
  /** Full CDN logo URL. */
  iconUri: string
  /** Minimum-deposit label, e.g. `$10 minimum`. */
  minLabel?: string
}

/**
 * Build the chain-picker rows from the curated chain list (the
 * `tokentransferconfig` chains resolved by `useSupportedTokens`, as chain-filter
 * chips), enriched with `@funkit/chains` logos and the per-chain minimum.
 *
 * The minimum mirrors web's `TransferToken`:
 *   `ceil(getMinTransferValueForChain(chainId, mintokentransfervalue) +
 *         getMinDepositUSD(target))`.
 */
export function buildAvailableChains({
  t,
  chainFilters,
  minTransferLimits,
  target,
}: {
  t: TFunction
  /** `useSupportedTokens().chainFilters` — `All` + one chip per curated chain. */
  chainFilters: TokenChainFilter[]
  minTransferLimits: MinTransferConfig
  target?: MinDepositTarget
}): AvailableChain[] {
  const minDepositAddOn = getConfiguredMinDepositUsd(target)

  return chainFilters
    .filter((f) => f.id !== 'all')
    .map((f) => ({ chainId: Number(f.id), name: f.label }))
    .map(({ chainId, name }) => {
      const min = getMinTransferValueUsd(
        chainId,
        minTransferLimits,
        minDepositAddOn,
      )
      return {
        id: String(chainId),
        name:
          name ||
          chainName(chainId) ||
          t('networks.chainFallback', { chainId }),
        iconUri: chainIconUri(chainId),
        minLabel: t('networks.minimum', { min }),
      }
    })
}
